import {
  useEnvironment,
  useTranslation,
  useExperiments,
  ControllerFlowAPI,
} from '@wix/yoshi-flow-editor';
import React, { useState, useEffect } from 'react';
import { CustomDialog, InputDialog, IconButton } from 'wix-ui-tpa/cssVars';
import {
  CartFlow,
  CartModalStatus,
  ReservedPaymentOptionIds,
} from '../../../../types/types';
import { useFormActions } from '../../Hooks/useFormActions';
import { CartModalDataHooks } from './dataHooks';
import { ModalContent } from './ModalContent/ModalContent';
import { ButtonProp, ModalFooter } from './ModalFooter/ModalFooter';
import { classes, st } from './CartModal.st.css';
import { MobileModal } from './MobileModal/MobileModal';
import { FormSelectedSlot } from '../../../../types/formSelectedSlots';
import { ServiceData, SlotService } from '../../../../utils/state/types';
import { mapToArray } from '../../../../utils';
import { ExperimentsConsts } from '../../../../consts/experiments';
import { getContent } from '../../../../utils/content/content';
import settingsParams from '../../settingsParams';
import { TagName } from '../../../../utils/tagName.const';
import Text, { TextType } from '../../Text/Text';
import { Close } from '@wix/wix-ui-icons-common/on-stage';
import { useFormComponentContext } from '../../Hooks/useFormComponentContext';

export interface CartModalProps {
  cartModalStatus: CartModalStatus;
  serviceData: ServiceData;
  formSelectedSlot: FormSelectedSlot;
  dateRegionalSettingsLocale: string;
  cartLineItems: any[];
  bookingIds?: string[];
  settings: ControllerFlowAPI['settings'];
}

const CartModal: React.FC<CartModalProps> = ({
  cartModalStatus,
  serviceData,
  dateRegionalSettingsLocale,
  formSelectedSlot,
  cartLineItems,
  bookingIds,
  settings,
}) => {
  const { t } = useTranslation();
  const { onCartCTAClick, upsellPluginSendBookingIds } = useFormActions();
  const { isMobile, isRTL } = useEnvironment();
  const { experiments } = useExperiments();
  const isSingleItemOnCart = cartLineItems?.length === 1;
  let shouldSecondaryButtonBeCheckout = false;
  const isOpen = cartModalStatus === CartModalStatus.OPEN;
  const slotService = mapToArray<SlotService>(serviceData.slotServices)[0];
  const { service, selectedPaymentOption, memberships } = slotService;
  const { isUpsellPluginInstalled } = useFormComponentContext();

  useEffect(() => {
    if (
      experiments.enabled(ExperimentsConsts.AdditionalTimeSlotsInFormPlugin)
    ) {
      if (isOpen) {
        upsellPluginSendBookingIds(bookingIds || []);
      }
    }
  }, [isOpen]);

  const isUseCustomDialogForCartModal = experiments.enabled(
    ExperimentsConsts.UseCustomDialogForCartModal,
  );

  if (isSingleItemOnCart) {
    const isFreePrice =
      service?.payment?.paymentDetails?.isFree &&
      selectedPaymentOption?.id === ReservedPaymentOptionIds.SingleSession;

    const isCustomPrice =
      service?.payment?.paymentDetails?.priceText &&
      selectedPaymentOption?.id === ReservedPaymentOptionIds.SingleSession;

    const isMembershipOptionSelected = !!memberships?.eligibleMemberships?.some(
      (membership) => selectedPaymentOption?.id === membership.id,
    );

    shouldSecondaryButtonBeCheckout =
      isSingleItemOnCart &&
      (isFreePrice ||
        isCustomPrice ||
        isMembershipOptionSelected ||
        service.isPendingApprovalFlow);
  }

  const onClose = () => onCartCTAClick(slotService, CartFlow.ADD_MORE_SESSIONS);

  const title = t('app.booking-details.cart-modal.title');

  const [primaryButtonLoading, setPrimaryButtonLoading] =
    useState<boolean>(false);
  const [secondaryaryButtonLoading, setSecondaryaryButtonLoading] =
    useState<boolean>(false);

  let primaryButton: ButtonProp | undefined;
  if (settings.get(settingsParams.addMoreSessionsButtonVisible)) {
    primaryButton = {
      dataHook: CartModalDataHooks.PRIMARY_BUTTON,
      text: getContent({
        settings,
        settingsParam: settingsParams.addMoreSessionsButtonText,
      }),
      onClick: async () => {
        setPrimaryButtonLoading(true);
        await onCartCTAClick(slotService, CartFlow.ADD_MORE_SESSIONS);
      },
      loading: primaryButtonLoading,
    };
  }

  const secondaryButton: ButtonProp = {
    dataHook: CartModalDataHooks.SECONDARY_BUTTON,
    text: t(
      shouldSecondaryButtonBeCheckout
        ? 'app.booking-details.cart-modal.cta.complete-checkout.label'
        : getContent({
            settings,
            settingsParam: settingsParams.viewCartButtonText,
          }),
    ),
    onClick: async () => {
      setSecondaryaryButtonLoading(true);
      await onCartCTAClick(slotService, CartFlow.CHECKOUT);
    },
    loading: secondaryaryButtonLoading,
  };

  if (isMobile) {
    return (
      <MobileModal
        primaryButton={primaryButton}
        secondaryButton={secondaryButton}
        serviceData={serviceData}
        formSelectedSlot={formSelectedSlot}
        dateRegionalSettingsLocale={dateRegionalSettingsLocale}
        isOpen={isOpen}
        onClose={onClose}
        title={title}
        dataHook={CartModalDataHooks.MOBILE_MODAL}
      />
    );
  }

  return isUseCustomDialogForCartModal ? (
    <CustomDialog
      className={st(classes.root, { rtl: isRTL })}
      data-hook={CartModalDataHooks.CUSTOM_DIALOG}
      isOpen={isOpen}
      onClose={onClose}
    >
      <div className={classes.dialogBase}>
        <div className={classes.closeButton}>
          <IconButton
            data-hook={CartModalDataHooks.CLOSE_BUTTON}
            icon={<Close />}
            onClick={onClose}
          />
        </div>
        <div className={[classes.margins, classes.marginBottom].join(' ')}>
          <Text tagName={TagName.Span} type={TextType.Title} isBlackOnly>
            {title}
          </Text>
        </div>
        <div
          className={[
            classes.contentWrapper,
            ...(isUpsellPluginInstalled ? [classes.marginBottom] : []),
          ].join(' ')}
        >
          <ModalContent
            serviceData={serviceData}
            formSelectedSlot={formSelectedSlot}
            dateRegionalSettingsLocale={dateRegionalSettingsLocale}
          />
        </div>
        <div className={[classes.margins, classes.footerWrapper].join(' ')}>
          <ModalFooter
            primaryButton={primaryButton}
            secondaryButton={secondaryButton}
          />
        </div>
      </div>
    </CustomDialog>
  ) : (
    <InputDialog
      className={classes.root}
      data-hook={CartModalDataHooks.INPUT_DIALOG}
      title={title}
      isOpen={isOpen}
      onClose={onClose}
      customFooter={
        <ModalFooter
          primaryButton={primaryButton}
          secondaryButton={secondaryButton}
        />
      }
    >
      <div className={classes.contentWrapper}>
        <ModalContent
          serviceData={serviceData}
          formSelectedSlot={formSelectedSlot}
          dateRegionalSettingsLocale={dateRegionalSettingsLocale}
        />
      </div>
    </InputDialog>
  );
};

export default CartModal;
